import * as React from "react";
import { FormControl as BaseFormControl, FormControlProps } from "baseui/form-control";
import { SIZE } from "baseui/input";

import { LabelMedium } from "../typography";
import { isEmpty } from "../utils/primitives";
import { maybe } from "../utils/functional";

import { hideLabelStyles } from "./utils";

/**
 * Wrap `Input`s in a label and help message.
 *
 * In addition to the charecteristics outlined in BaseWeb's
 * documentation, this `FormControl` manages floating
 * labels/placeholders.
 */

export type TFormControlProps = {
  forceFocus?: boolean;
  hideLabel?: boolean;
  title?: string;
  help?: string;
  captionAlign?:
    | "start"
    | "end"
    | "left"
    | "right"
    | "center"
    | "justify"
    | "match-parent";
  initialLoad?: boolean;
  verticalOffset?: boolean;
} & FormControlProps;

export const FormControl = (props: TFormControlProps) => {
  const centerAlignComponents = [
    "BooleanInput",
    "Checkbox",
    "ButtonRadioInput",
    "CardSelect",
  ];
  const {
    label,
    title,
    children,
    forceFocus,
    hideLabel,
    initialLoad,
    // Captions/errors are center aligned by default for dynamic width components. These
    // components take full width of parent but the actual content may be smaller and
    // center aligned.
    // Aligning the errors to left will make error to show on left but the content is center.
    // More information here https://app.clickup.com/t/2703166/DEV-3582
    captionAlign = props.captionAlign ||
    centerAlignComponents.includes(props?.children?.type?.name)
      ? "center"
      : "left",
    verticalOffset, // Whether or not we want labels to be centered on the input or above it (default is above it)
    ...restProps
  } = props;
  const [focused, setFocused] = React.useState(forceFocus);
  const [empty, setIsEmpty] = React.useState(isEmpty(children.props.value));
  const inputRef = React.useRef(null);
  function focusInput() {
    if (label) {
      inputRef.current && inputRef.current.focus();
    }
  }
  function handleFocus(childOnFocus) {
    return (...args) => {
      setFocused(true);
      maybe(childOnFocus)(...args);
    };
  }
  function handleBlur(childOnBlur) {
    return (...args) => {
      setFocused(forceFocus || false);
      // Don't blur on initial load so that useForm's onBlur is not triggered and don't
      // set touched to true and error is not shown on initial load.
      if (!initialLoad) {
        maybe(childOnBlur)(...args);
      }
    };
  }
  function handleChange(event, onChange) {
    const value = event && event.target ? event.target.value : event;
    setIsEmpty(isEmpty(value));
    maybe(onChange)(event);
  }
  return (
    <>
      {title && (
        <LabelMedium center marginBottom="scale800">
          {title}
        </LabelMedium>
      )}
      <BaseFormControl
        label={label}
        overrides={{
          Label: {
            style: ({ $theme }) =>
              getLabelStyle({
                theme: $theme,
                size: children.props.size,
                label,
                title,
                focused,
                empty,
                hideLabel,
                verticalOffset,
              }),
            props: {
              onClick: focusInput,
            },
          },
          ControlContainer: {
            style: {
              marginBottom: 0,
            },
          },
          Caption: {
            style: {
              marginBottom: 0,
              textAlign: captionAlign,
              marginLeft: "auto",
              marginRight: "auto",
            },
          },
        }}
        {...restProps}
      >
        {React.cloneElement(React.Children.only(children), {
          ...(label && !(hideLabel || forceFocus) ? { placeholder: null } : {}),
          inputRef,
          onFocus: handleFocus(children.props.onFocus),
          onBlur: handleBlur(children.props.onBlur),
          onChange: (event) => handleChange(event, children.props.onChange),
          setIsEmpty,
        })}
      </BaseFormControl>
    </>
  );
};

export function getLabelStyle(args = {}) {
  // TODO types
  const {
    theme,
    label,
    focused,
    empty,
    size,
    title,
    hideLabel = false,
    verticalOffset,
  } = args;
  // NOTE: The width and marginRight values below ensures the label
  // does not extend over the top of buttons on the right of the
  // Input. In particular, this is needed for the DateInput.
  let style = {
    width: "calc(100% - 32px)",
    marginRight: "32px",
    transition: "all 0.4s",
    fontWeight: 600,
    fontSize: "12px",
  };
  const fontSize = getFont(size, theme.typography).fontSize;
  if (hideLabel || (focused && !!title)) {
    style = {
      ...style,
      ...hideLabelStyles(),
    };
  } else if (label && !focused && empty) {
    style = {
      ...style,
      cursor: "text",
      fontSize,
      fontWeight: 400,
      transform: verticalOffset
        ? "translate(0,0)"
        : `translate(0,calc(${fontSize} + 9px))`,
      color: theme.colors.contentTertiary,
    };
  }
  return style;
}

function getFont(size = SIZE.default, typography) {
  return {
    [SIZE.mini]: typography.font100,
    [SIZE.compact]: typography.font200,
    [SIZE.default]: typography.font300,
    [SIZE.large]: typography.font400,
  }[size];
}
